import { useEffect, useState } from 'react';
import { Alert, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link, useParams } from 'react-router';
import api from '../../../api';
import { draftFrom } from '../../../domain/matchDayDraft';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import Step2 from '../points/Step2';
import Step3 from '../points/Step3';
import Step4 from '../points/Step4';
import Step5 from '../points/Step5';
import Loading from '../../Loading';
import ErrorList from '../../ErrorList';

const LABELS: Record<number, string> = {
  2: 'Wyniki', 3: 'Kary', 4: 'Komentarz', 5: 'Podsumowanie'
};

const SAVING_OFFSET = 100;

const stepFor = (step: number) => {
  switch (step) {
    case 2: return <Step2 />;
    case 3: return <Step3 />;
    case 4: return <Step4 />;
    case 5: return <Step5 />;
    default: return step >= SAVING_OFFSET ? <Loading /> : null;
  }
};

export default function EditMatchDay() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { creatorStep, errorMessages, editingId } = useAppSelector((s) => s.points);
  const [error, setError] = useState<string | null>(null);
  const [when, setWhen] = useState<string | null>(null);

  useEffect(() => {
    if (!id) {
      return undefined;
    }
    let live = true;
    api.matchDay(id)
      .then((response) => {
        if (!live) {
          return;
        }
        if (response.statusCode === 200 && response.matchDay) {
          setWhen(new Date(response.matchDay.date).toLocaleDateString('pl-PL', {
            weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric'
          }));
          dispatch({ type: 'POINTS_LOAD_DRAFT', ...draftFrom(response.matchDay) });
        } else {
          setError(response.messages.join(' ') || 'Nie udało się pobrać dnia meczowego');
        }
      })
      .catch((e: Error) => { if (live) { setError(e.message); } });
    return () => { live = false; };
  }, [id, dispatch]);

  if (error) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Alert severity="error">{error}</Alert>
        <Button component={Link} to="/admin/match-days">Wróć do listy</Button>
      </Stack>
    );
  }

  if (editingId !== id) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Wczytywanie…</Typography>
      </Stack>
    );
  }

  if (creatorStep === 6) {
    return (
      <Stack spacing={2} alignItems="flex-start">
        <Alert severity="success">Dzień meczowy zapisany.</Alert>
        <Button component={Link} to="/admin/match-days" variant="contained">
          Wróć do listy
        </Button>
      </Stack>
    );
  }

  const active = creatorStep >= SAVING_OFFSET ? creatorStep - SAVING_OFFSET : creatorStep;

  return (
    <Stack className="editMatchDay" spacing={2}>
      <div>
        <Typography variant="h6">Edytuj dzień meczowy</Typography>
        <Typography variant="body2" color="text.secondary">
          {when} · {LABELS[active] ?? ''}
        </Typography>
      </div>
      {/* Deliberately not editable: changing who played would regenerate the Matches and
          discard every score, and moving the date could make this Match Day someone else's
          "previous". Both are refused by the backend anyway. */}
      <Alert severity="info">
        Zmienić można wyniki, kary i komentarz. Data i lista osób są stałe.
      </Alert>
      <ErrorList errorMessages={errorMessages} />
      {stepFor(creatorStep)}
      <Button component={Link} to="/admin/match-days" size="small">Anuluj</Button>
    </Stack>
  );
}
